/** @file Consumer details, activity, trade-ins, and manual masterdata potential creation. */
import {FC, useState} from "react";
import Page from "../../../../layout/Page";
import PageHeader from "../../../../layout/PageHeader";
import PageContent from "../../../../layout/PageContent";
import PageGroup from "../../../../layout/PageGroup";
import {useParams} from "react-router-dom";
import useConsumer from "./hooks/useConsumer";
import {LinearProgress} from "@mui/material";
import {ConsumerPanel} from "../../components/ConsumerPanel";
import {ConsumerTradeIns} from "./views/ConsumerTradeIns";
import {TitleText} from "../../../../layout/Typography";
import {ConsumerEvents} from "../../../brand/ConsumerEvents";
import Button from "@mui/material/Button";
import {PotentialsList} from "../../components/PotentialsList";
import {CircularPointsPanel} from "../../components/CircularPointsPanel";
import {PotentialAddDialog} from "./PotentialAddDialog";
import {Potential, useConsumerPageFetchPotentialsMutation} from "../../../../Queries";
import {SavingUI} from "../../../../SavingProvider";
import {ConsumerEditForm} from "./ConsumerEditForm";

export const ConsumerPagePath = (brandHouseId: string, shopId: string, consumerId: string) => {
   return "/brands/" + brandHouseId + "/consumers/" + shopId + "/" + consumerId
}

/** Displays a consumer and refreshes their potential list after adding a masterdata variant. */
export const ConsumerPage: FC<any> = () => {
   const {
      brandHouseId,
      shopId,
      consumerId
   } = useParams<{ brandHouseId: string, shopId: string, consumerId: string }>();
   const consumer = useConsumer(consumerId)
   const [addPotential, setAddPotential] = useState<boolean>(false)
   const [potentialsRevision, setPotentialsRevision] = useState(0)
   const [editConsumer, setEditConsumer] = useState<boolean>(false)
   const [fetchPotentials] = useConsumerPageFetchPotentialsMutation()

   if (!consumer) {
      return <LinearProgress/>
   }

   const onPotentialSelect = (potential: Potential) => {

   }

   /** Closes the picker and reloads the list so the new potential appears immediately. */
   const onPotentialCreated = () => {
      setAddPotential(false)
      setPotentialsRevision(revision => revision + 1)
   }

   const onFetchPotentials = async () => {
      await SavingUI.process(async () => {
         await fetchPotentials({
            variables: {
               shopId,
               consumerId
            }
         })
      }, 'Fetching')
   }

   const onAddPotential = () => {
      setAddPotential(true)
   }

   const onEditConsumer = () => {
      setEditConsumer(true)
   }

   const onConsumerSaved = () => {
      window.location.reload(); // Reload to show updated data
   }

   return <Page>
      <PageHeader title={consumer.firstName + ' ' + consumer.lastName} onBack={true}/>

      <PageGroup>
         <PageContent>
            <TitleText type={"h2"}>Activity</TitleText>
            <ConsumerEvents consumerId={consumerId} options={{
               height: 200,
               hideHeader: true
            }}/>
         </PageContent>
         <PageContent commands={[
            <Button key={"editConsumer"} onClick={onEditConsumer}>Edit Consumer</Button>
         ]}>
            <ConsumerPanel consumerId={consumerId} hideDetailsLink={true}/>
         </PageContent>
      </PageGroup>

      <PageContent>
         <TitleText sx={{marginBottom: '20px'}} type={"h2"}>Trade-ins</TitleText>
         <ConsumerTradeIns consumerId={consumerId}/>
      </PageContent>
      <PageContent commands={[
         <Button key={"fetchPotentials"} onClick={onFetchPotentials}>Fetch potentials</Button>,
         <Button key={"addPotential"} onClick={onAddPotential}>Add</Button>
      ]}>
         <TitleText type={"h2"}>Potential trade-ins</TitleText>
         <PotentialsList key={potentialsRevision} shopId={shopId} onPotentialSelect={onPotentialSelect} consumerId={consumerId}
                         hideTradeIns={true}/>
      </PageContent>
      <PageContent>
         <TitleText type={"h2"}>Circular Points</TitleText>
         <CircularPointsPanel consumerId={consumerId}/>
      </PageContent>
      {addPotential && <PotentialAddDialog
         brandHouseId={brandHouseId} consumerId={consumerId} onCancel={() => setAddPotential(false)}
         onCreated={onPotentialCreated}
      />}
      {editConsumer && consumer && (
         <ConsumerEditForm
            consumer={consumer}
            open={editConsumer}
            onClose={() => setEditConsumer(false)}
            onSave={onConsumerSaved}
         />
      )}
   </Page>
}
